import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Megaphone, Paperclip, Loader2 } from "lucide-react";
import { crDateTime } from "@/lib/dateFormat";
import { toast } from "sonner";
import { useI18n } from "@/i18n/i18n";

const PUBLICOS = {
  superadmin: ["all", "teachers", "students", "affiliates", "group", "sede"],
  academic_director: ["all", "teachers", "students", "group", "sede"],
  teacher: ["my_students", "group"],
};

/** Un mismo mensaje para varias personas. A cada una le llega como mensaje privado,
 *  así que si responden, le responden solo a quien lo envió. */
export const AnnouncementDialog = ({ role, onSent }) => {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [audience, setAudience] = useState((PUBLICOS[role] || ["students"])[0]);
  const [groupId, setGroupId] = useState("");
  const [sedeId, setSedeId] = useState("");
  const [withEmail, setWithEmail] = useState(false);
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const [groups, setGroups] = useState([]);
  const [sedes, setSedes] = useState([]);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    if (!open) return;
    api.get("/groups").then((r) => setGroups(r.data || [])).catch(() => {});
    api.get("/sedes").then((r) => setSedes(r.data || [])).catch(() => {});
    api.get("/broadcasts").then((r) => setHistory(r.data || [])).catch(() => {});
  }, [open]);

  const send = async () => {
    if (!text.trim()) return toast.error(t("ann_need_text"));
    if (audience === "group" && !groupId) return toast.error(t("ann_need_group"));
    if (audience === "sede" && !sedeId) return toast.error(t("ann_need_sede"));
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("text", text.trim());
      fd.append("audience", audience);
      fd.append("group_id", groupId);
      fd.append("sede_id", sedeId);
      fd.append("send_email_copy", withEmail ? "true" : "false");
      if (file) fd.append("file", file);
      const r = await api.post("/broadcasts", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(t("ann_sent").replace("{n}", r.data.recipients));
      setText(""); setFile(null);
      setHistory((prev) => [r.data, ...prev]);
      setOpen(false);
      onSent?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("ann_error"));
    } finally {
      setSending(false);
    }
  };

  const opciones = PUBLICOS[role] || [];
  if (!opciones.length) return null;

  return (
    <>
      <Button size="sm" onClick={() => setOpen(true)} data-testid="announcement-open-btn"
              className="bg-emerald-600 hover:bg-emerald-700 text-white">
        <Megaphone className="h-4 w-4" /> {t("ann_button")}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl max-h-[88vh] overflow-y-auto" data-testid="announcement-dialog">
          <DialogHeader><DialogTitle>{t("ann_title")}</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">{t("ann_help")}</p>

          <div className="space-y-3 mt-1">
            <div>
              <Label className="text-xs">{t("ann_audience")}</Label>
              <Select value={audience} onValueChange={setAudience}>
                <SelectTrigger className="mt-1" data-testid="announcement-audience">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {opciones.map((o) => (
                    <SelectItem key={o} value={o}>{t(`ann_aud_${o}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {audience === "group" && (
              <div>
                <Label className="text-xs">{t("ann_pick_group")}</Label>
                <Select value={groupId} onValueChange={setGroupId}>
                  <SelectTrigger className="mt-1" data-testid="announcement-group">
                    <SelectValue placeholder={t("ann_pick_group")} />
                  </SelectTrigger>
                  <SelectContent className="max-h-64">
                    {groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            {audience === "sede" && (
              <div>
                <Label className="text-xs">{t("ann_pick_sede")}</Label>
                <Select value={sedeId} onValueChange={setSedeId}>
                  <SelectTrigger className="mt-1" data-testid="announcement-sede">
                    <SelectValue placeholder={t("ann_pick_sede")} />
                  </SelectTrigger>
                  <SelectContent>
                    {sedes.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label className="text-xs">{t("ann_message")}</Label>
              <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={5}
                        className="mt-1" placeholder={t("ann_placeholder")}
                        data-testid="announcement-text" />
            </div>

            <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 px-3 py-2">
              <div>
                <p className="text-sm font-medium text-slate-800">{t("ann_email_toggle")}</p>
                <p className="text-xs text-slate-500">{t("ann_email_hint")}</p>
              </div>
              <Switch checked={withEmail} onCheckedChange={setWithEmail}
                      data-testid="announcement-email-switch" />
            </div>

            <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
              <Paperclip className="h-4 w-4 text-slate-400" />
              <span>{file ? file.name : t("ann_attach")}</span>
              <input type="file" className="hidden" data-testid="announcement-file"
                     onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </label>

            <Button onClick={send} disabled={sending} className="w-full"
                    data-testid="announcement-send-btn">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Megaphone className="h-4 w-4" />}
              {t("ann_send")}
            </Button>
          </div>

          {history.length > 0 && (
            <div className="mt-4 border-t border-slate-200 pt-3">
              <p className="text-xs font-semibold text-slate-600 mb-2">{t("ann_history")}</p>
              <div className="space-y-2 max-h-48 overflow-y-auto" data-testid="announcement-history">
                {history.map((a) => (
                  <div key={a.id} className="text-xs text-slate-600 rounded-lg bg-slate-50 px-3 py-2">
                    <span className="font-medium text-slate-800">{t(`ann_aud_${a.audience}`)}</span>
                    {" · "}{a.recipients} {t("ann_people")}{a.email_sent ? ` · ${t("ann_with_email")}` : ""}
                    {" · "}{crDateTime(a.created_at)}
                    <p className="text-slate-500 truncate mt-0.5">{a.text}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
